// One-time migration / cleanup of legacy localStorage persisted Zustand state.
// Safari was throwing QUOTA_EXCEEDED_ERR due to large stale entries kept in localStorage
// from older versions (before moving to IndexedDB via localforage). We clear or migrate
// those keys early, before any store initializes, so that persist middleware doesn't
// attempt to read huge payloads or re-write them into a full storage area.

import { persistStorage } from './persistStorage';

// Only target the heavy data sets that were moved to IndexedDB. Leave lightweight UI state
// (program, trip-program, popup) in localStorage to keep fast access and avoid unnecessary churn.
const LEGACY_KEYS = ['places', 'recommended', 'favorites'];

const MIGRATION_FLAG = 'legacy_migrated';
const MIGRATION_FLAG_VERSION = '2'; // bump to re-run migration if needed in future

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

async function migrateKey(name, value) {
  // We only migrate the larger program/favorites data that might still be useful to users.
  // Smaller UI state like 'popup' is simply discarded.
  const SHOULD_MIGRATE = LEGACY_KEYS.includes(name);
  if (!SHOULD_MIGRATE) return; // discard others
  try {
    // Zustand persist stores raw JSON string; we can directly re-store it using our custom storage.
    await persistStorage.setItem(name, value);
  } catch (e) {
    // Ignore; fallback memory store inside persistStorage may capture later writes.

    console.warn(
      '[legacyMigration] Failed to re-store key in new storage',
      name,
      e,
    );
  }
}

export async function runLegacyMigration() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined')
    return;

  try {
    if (localStorage.getItem(MIGRATION_FLAG) === MIGRATION_FLAG_VERSION) return; // already done
  } catch {
    // If even reading fails (rare in hardened Safari private contexts), abort.
    return;
  }

  const toRemove = [];
  for (const key of LEGACY_KEYS) {
    let value = null;
    try {
      value = localStorage.getItem(key);
    } catch (e) {
      // QUOTA errors can even appear on getItem in some edge Safari versions; proceed.
    }
    if (value != null) {
      // Basic heuristic: ignore absurdly large corrupted strings (> ~5MB) to avoid memory spikes.
      if (value.length > 5 * 1024 * 1024) {
        console.warn(
          '[legacyMigration] Dropping oversized legacy key',
          key,
          value.length,
        );
      } else {
        // Try to ensure it's valid JSON (Zustand persist stores JSON strings); if not, skip migration.
        const parsed = safeParse(value);
        if (parsed !== null) {
          await migrateKey(key, value);
        }
      }
      toRemove.push(key);
    }
  }

  // Remove legacy keys to reclaim space.
  for (const key of toRemove) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  // Mark migration done (even if localStorage is mostly broken, this write may still succeed).
  try {
    localStorage.setItem(MIGRATION_FLAG, MIGRATION_FLAG_VERSION);
  } catch {
    // ignore
  }
}

// Kick off migration immediately (fire & forget). Components can await runLegacyMigration() if needed.
runLegacyMigration();

export default runLegacyMigration;
